import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, Globe, Smartphone, Monitor, Tablet, RefreshCw, LogOut,
  Loader2, Search, ShieldOff, Wifi, WifiOff, Clock,
} from "lucide-react";

const DeviceIcon = ({ type, className = "w-4 h-4" }) => {
  if (type === "Smartphone") return <Smartphone className={className} />;
  if (type === "Tablet") return <Tablet className={className} />;
  return <Monitor className={className} />;
};

const ROLE_COLOR = {
  admin: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  agent: "border-blue-500/40 text-blue-300 bg-blue-500/10",
  magazziniere: "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10",
  client: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
};

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diffMs / 1000);
    if (s < 60) return `${s}s fa`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    const d = Math.floor(h / 24);
    return `${d}g fa`;
  } catch {
    return "—";
  }
}

export default function AdminActiveSessions() {
  const { authAxios } = useAuth();
  const [data, setData] = useState({ total: 0, online: 0, sessions: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [onlyOnline, setOnlyOnline] = useState(true);
  const intervalRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await authAxios.get("/admin/sessions/active");
      setData(r.data);
    } catch (err) {
      if (!silent) toast.error("Errore caricamento sessioni");
      console.error("[ActiveSessions] load failed:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    load();
    // Auto-refresh ogni 30 secondi (silenzioso)
    intervalRef.current = setInterval(() => load(true), 30000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [load]);

  const handleForceLogout = async (session) => {
    try {
      await authAxios.delete(`/admin/sessions/${session.id}`);
      toast.success(`${session.user_name} disconnesso`);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore disconnessione");
    }
  };

  const filtered = data.sessions.filter((s) => {
    if (onlyOnline && !s.is_online) return false;
    if (roleFilter !== "all" && s.user_role !== roleFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      s.user_name.toLowerCase().includes(q) ||
      s.user_email.toLowerCase().includes(q) ||
      (s.ip || "").includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <PageHeader title="Sessioni Attive" />

        {/* KPI */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="bg-zinc-900/60 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-1">
                <Wifi className="w-3.5 h-3.5 text-emerald-400" />
                Online ora
              </div>
              <div className="text-3xl font-bold text-emerald-400" data-testid="kpi-online">
                {data.online}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                attivi negli ultimi {data.online_window_min || 5} min
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-1">
                <Users className="w-3.5 h-3.5" />
                Totale sessioni
              </div>
              <div className="text-3xl font-bold text-white" data-testid="kpi-total">
                {data.total}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">incluse dormienti</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtri */}
        <Card className="bg-zinc-900/60 border-white/10 mb-4">
          <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Cerca nome, email o IP…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-zinc-950 border-white/10"
                data-testid="sessions-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="sm:w-40 bg-zinc-950 border-white/10" data-testid="sessions-role-filter">
                <SelectValue placeholder="Tutti i ruoli" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i ruoli</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="agent">Agenti</SelectItem>
                <SelectItem value="magazziniere">Magazzinieri</SelectItem>
                <SelectItem value="client">Clienti</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOnlyOnline((v) => !v)}
              className={`border-white/10 ${onlyOnline ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "text-zinc-400"}`}
              data-testid="sessions-only-online-btn"
            >
              {onlyOnline ? <Wifi className="w-4 h-4 mr-1" /> : <WifiOff className="w-4 h-4 mr-1" />}
              {onlyOnline ? "Solo online" : "Tutte"}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => load()}
              disabled={loading}
              className="border-white/10"
              data-testid="sessions-reload-btn"
              title="Ricarica"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </CardContent>
        </Card>

        {/* Lista sessioni */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Caricamento sessioni…
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-12 text-center text-zinc-500">
              Nessuna sessione trovata.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {filtered.map((s) => (
              <Card
                key={s.id}
                className={`bg-zinc-900/60 border-white/10 hover:border-amber-500/20 transition-colors ${s.is_online ? "" : "opacity-60"}`}
              >
                <CardContent className="p-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`w-2 h-2 rounded-full ${s.is_online ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`}
                          title={s.is_online ? "Online" : "Offline / dormiente"}
                        />
                        <span className="font-semibold text-white truncate">
                          {s.user_name}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${ROLE_COLOR[s.user_role] || ""}`}
                        >
                          {s.user_role}
                        </Badge>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span>{s.user_email}</span>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <DeviceIcon type={s.device_short} className="w-3.5 h-3.5 text-zinc-500" />
                          {s.browser} {s.os}
                        </span>
                        <span className="flex items-center gap-1 font-mono">
                          <Globe className="w-3.5 h-3.5 text-zinc-500" />
                          {s.ip}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-zinc-500" />
                          {timeAgo(s.last_active_at)}
                        </span>
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
                          data-testid={`force-logout-btn-${s.id}`}
                          title="Forza logout"
                        >
                          <LogOut className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Disconnetti</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white flex items-center gap-2">
                            <ShieldOff className="w-5 h-5 text-red-400" />
                            Disconnettere {s.user_name}?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            La sua sessione verrà chiusa entro 1 minuto. Dovrà eseguire di nuovo il login per accedere all&apos;app.
                            <br />
                            <span className="text-xs text-zinc-500 mt-2 block font-mono">
                              IP: {s.ip} · {s.device}
                            </span>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-zinc-800 border-white/10">Annulla</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleForceLogout(s)}
                            className="bg-red-500 hover:bg-red-600 text-white"
                            data-testid={`confirm-logout-${s.id}`}
                          >
                            Disconnetti
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-[11px] text-zinc-600 mt-4 text-center">
          Lista aggiornata automaticamente ogni 30 secondi.
        </p>
      </div>
    </div>
  );
}
